import { getActiveSessionManager } from '@bounded-sh/core';
import { establishInlineSession, inlineAuthEndpoint } from './oidc-auth';
import { openHiddenSignerFrame } from './turnkey-signer-frame';
import { requestTurnkeyActionAuthorization } from './turnkey-signing-capability';
function hasDOM() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
let requestSeq = 0;
// Seal the OTP in the given hidden signer iframe on the Bounded origin. The signer page
// runs the vendored @turnkey/crypto encryptOtpCodeToBundle (which verifies the enclave's
// signature on the target bundle first) and KEEPS the sealing keypair for the follow-up
// establishSigningSession exchange below.
async function sealOtpInFrame(frame, code, otpEncryptionTargetBundle) {
    const d = await frame.request({
        type: 'bounded:turnkey:encryptOtp',
        requestId: `tkotp_${Date.now()}_${++requestSeq}`,
        otpCode: code,
        otpEncryptionTargetBundle,
    }, 30000).catch(() => { throw new Error('Securing the code timed out. Please try again.'); });
    if (d.ok && typeof d.encryptedOtpBundle === 'string' && d.encryptedOtpBundle) {
        return d.encryptedOtpBundle;
    }
    throw new Error(String(d.error || 'Could not secure the code. Please try again.'));
}
// Best-effort login-time signing-session establishment. Runs AFTER the login session is
// live (the action authorization is minted from the freshly-established id token) against
// the SAME frame that sealed the code - only that page instance holds the keypair the
// verificationToken is bound to. Owns the frame: disposes it whatever happens. Never
// throws; a failure only means the first signature costs one inline code.
async function establishSigningSessionInBackground(frame, baseUrl, signingSession) {
    // The moment this work begins - captured before ANY await, because verify() has already
    // resolved and the user can hit logout throughout the authorization mint below. The
    // signer drops a session whose work began at-or-before the newest logout, so a stamp
    // taken later (in the frame, after the mint) would let this establishment out-run a
    // logout and resurrect the session.
    const startedAtMs = Date.now();
    try {
        let idToken = null;
        try {
            idToken = getActiveSessionManager().getIdToken();
        }
        catch (_a) {
            idToken = null;
        }
        if (!idToken)
            return;
        const authorization = await requestTurnkeyActionAuthorization({
            baseUrl,
            idToken,
            action: 'createWallet',
            fetchImpl: (url, init) => fetch(url, init),
        });
        const d = await frame.request({
            type: 'bounded:turnkey:establishSigningSession',
            requestId: `tkestablish_${Date.now()}_${++requestSeq}`,
            verificationToken: signingSession.verificationToken,
            authorization,
            startedAtMs,
        }, 20000);
        if (!d.ok)
            throw new Error(String(d.error || 'establish_failed'));
    }
    catch (_b) {
        /* best-effort: the signer's inline code flow covers the first signature */
    }
    finally {
        frame.dispose();
    }
}
/**
 * Begin authMode:"turnkey" login: ask Turnkey to email a one-time code to `email`.
 * Resolves with a handle whose `verify(code)` finishes the login. Web-only (the sealing
 * step needs the signer iframe); on React Native use loginWithRedirect() instead.
 */
export async function startTurnkeyEmailLogin(email) {
    if (!hasDOM()) {
        throw new Error('Turnkey-native email login is web-only - on React Native use loginWithRedirect()');
    }
    const trimmed = email.trim();
    if (!trimmed)
        throw new Error('Enter your email to continue.');
    const { base, appId } = await inlineAuthEndpoint();
    if (!appId)
        throw new Error('appId is not configured (init the SDK with your appId)');
    const res = await fetch(`${base}/wallet/turnkey/auth/init`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: trimmed, appId }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || typeof data.otpId !== 'string' || typeof data.otpEncryptionTargetBundle !== 'string') {
        throw new Error(String((data && data.error) || 'Could not send a code. Please try again.'));
    }
    const otpId = data.otpId;
    const otpEncryptionTargetBundle = data.otpEncryptionTargetBundle;
    return {
        email: trimmed,
        async verify(code) {
            const trimmedCode = (code || '').trim();
            if (!trimmedCode)
                throw new Error('Enter the code from your email.');
            // One frame per attempt: the sealing keypair lives in this exact page instance,
            // and a failed attempt's frame (and keypair) is discarded with it.
            const frame = await openHiddenSignerFrame();
            let vdata = null;
            try {
                const encryptedOtpBundle = await sealOtpInFrame(frame, trimmedCode, otpEncryptionTargetBundle);
                const vres = await fetch(`${base}/wallet/turnkey/auth/verify`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ email: trimmed, appId, otpId, encryptedOtpBundle }),
                });
                vdata = await vres.json().catch(() => null);
                if (!vres.ok || !vdata) {
                    throw new Error(String((vdata && vdata.error) || 'Invalid or expired code.'));
                }
            }
            catch (error) {
                frame.dispose();
                throw error;
            }
            const user = await establishInlineSession(vdata).catch((error) => {
                frame.dispose();
                throw error;
            });
            // SINGLE CODE AT LOGIN: turn the same code's verificationToken into the 24h
            // signing session, in the background - login is already resolved and must never
            // wait on (or fail with) this. The task owns the frame's disposal.
            const signingSession = vdata.signingSession;
            if (signingSession && typeof signingSession.verificationToken === 'string' && signingSession.verificationToken) {
                void establishSigningSessionInBackground(frame, base, {
                    verificationToken: signingSession.verificationToken,
                });
            }
            else {
                frame.dispose();
            }
            return user;
        },
    };
}
